import { PlanetLite } from '@apeiron/planet'
import { NextImage, noForwardCustomProps } from '@apeiron/temp'
import { Box, Button, styled, Typography } from '@mui/material'
import ArrowSVG from '@public/icons/icon-arrow-right-circle.svg'
import ApeironCardStatus from '@src/components/share/apeiron/ApeironCardStatus'
import DateLinearProgress from '@src/components/share/generic/DateLinearProgress'
import { FC, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

const Container = styled(
  ApeironCardStatus,
  noForwardCustomProps(['disabled'])
)<{
  disabled: boolean
}>`
  position: absolute;
  width: 95%;
  height: 13%;
  bottom: 6.2%;

  .Custom-Upper-Background {
    width: 97%;
    flex: 3.1;
    background: ${props =>
      props.disabled
        ? ` linear-gradient(
              90deg,
              rgba(255, 77, 0, 0.2) 0%,
              rgba(255, 77, 0, 0.7) 11%,
              rgba(255, 77, 0, 0.95) 50%,
              rgba(255, 77, 0, 0.7) 89%,
              rgba(255, 77, 0, 0.2) 100%
            )`
        : ` linear-gradient(
              90deg,
              rgba(8, 59, 237, 0.2) 0%,
              rgba(8, 59, 237, 0.7) 11%,
              rgba(8, 59, 237, 0.95) 50%,
              rgba(8, 59, 237, 0.7) 89%,
              rgba(8, 59, 237, 0.2) 100%
            )`};
  }
  .Custom-Lower-Background {
    width: 86%;
    flex: 1.3;
    background: ${props =>
      props.disabled
        ? ` linear-gradient(
              90deg,
              rgba(255, 77, 0, 0.2) 0%,
              rgba(255, 77, 0, 0.7) 11%,
              rgba(255, 77, 0, 0.95) 50%,
              rgba(255, 77, 0, 0.7) 89%,
              rgba(255, 77, 0, 0.2) 100%
            )`
        : ` linear-gradient(
              90deg,
              rgba(8, 59, 237, 0.2) 0%,
              rgba(8, 59, 237, 0.7) 11%,
              rgba(8, 59, 237, 0.95) 50%,
              rgba(8, 59, 237, 0.7) 89%,
              rgba(8, 59, 237, 0.2) 100%
            )`};
  }
`

const StyledButton = styled(Button)`
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-radius: 0px;
  width: 100%;
  height: 100%;
`

const TitleContainer = styled(Box, noForwardCustomProps(['disabled']))<{
  disabled: boolean
}>`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  ${props => props.disabled && 'padding-left: 10px;'}
`

const Arrow = styled(NextImage)`
  aspect-ratio: 1;
  margin-top: -3px;
  width: 18px;
`

const Title = styled(Typography)`
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #ffffff;
  margin-bottom: 2px;
  text-align: center;
  text-transform: none;
`

const ProgressBackground = styled(Box)`
  width: 70%;
  background-color: #151615;
  border-radius: 10px;
  padding: 3px;
`

const PlanetCardStatus: FC<Props> = props => {
  const { arrow = true, className, now, planet, onClick } = props

  const { bottomStatus, disabled } = planet

  const disabledPlanet = !!disabled

  const { t } = useTranslation()

  const showArrow = arrow && disabledPlanet

  const handleOnClick = useCallback(() => {
    if (onClick && showArrow) {
      onClick(planet)
    }
  }, [onClick, planet, showArrow])

  return bottomStatus ? (
    <Container disabled={disabledPlanet} className={className}>
      <StyledButton disableRipple onClick={handleOnClick}>
        <TitleContainer disabled={disabledPlanet}>
          <Title>{t(bottomStatus.translationKey)}</Title>
          {showArrow && <Arrow src={ArrowSVG} />}
        </TitleContainer>
        {bottomStatus.start && bottomStatus.end && (
          <ProgressBackground>
            <DateLinearProgress
              startAt={bottomStatus.start}
              endAt={bottomStatus.end}
              now={now}
            />
          </ProgressBackground>
        )}
      </StyledButton>
    </Container>
  ) : (
    <></>
  )
}

type Props = {
  arrow?: boolean
  className?: string
  now: Date
  planet: PlanetLite
  onClick?: (planet: PlanetLite) => void
}

export default PlanetCardStatus
